import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'

import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { DXTRADE_POSITION_EFFECT } from '../../utils/constants'
import { getStepDecimals } from '../../utils/helpers'
import { OrderPnL } from '../components/OrderPnL'
import { useClosePrice } from '../hooks/useClosePrice'
import { useDxtradeOrderCashAdjustments } from '../hooks/useDxtradeOrderCashAdjustments'
import type { HistoryOrderWithLots } from '../hooks/useHistoryOrdersWithLots'
import { useOrderNetPnl } from '../hooks/useOrderNetPnl'
import type { OvernightFees } from '../utils/DxtradeCashTransfersFields'
import {
  formatDateTime,
  formatSignedUsd,
  formatSymbol,
  renderCellValue,
  renderPnl,
  renderSide,
} from '../utils/formatters'

type TradeDetailsHistoryModalProps = {
  order: HistoryOrderWithLots
  accountCurrency?: CurrencyType
  overnightFees?: OvernightFees | null
}

export const TradeDetailsHistoryModal: BaseModalType<TradeDetailsHistoryModalProps> = (props) => {
  const { order, accountCurrency, overnightFees: resolvedOvernightFees } = props
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const fractionDigits = getStepDecimals(order.priceIncrement)

  const sizeLots = order.sizeLots
  const { commission } = useDxtradeOrderCashAdjustments(order)
  const closePrice = useClosePrice(order)
  const netPnl = useOrderNetPnl(order, resolvedOvernightFees)
  const renderedNetPnl = renderPnl(netPnl, Body2, accountCurrency)
  const positionEffect = order.legs?.[0]?.positionEffect

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Trade details')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between'>
              <Body1 color='text-high-em' medium>
                {formatSymbol(order.instrument, order.description)}
              </Body1>
              <SensitiveData>
                {positionEffect === DXTRADE_POSITION_EFFECT.CLOSE && (
                  <OrderPnL
                    order={order}
                    accountCurrency={accountCurrency}
                    mode='close'
                    Typography={(typographyProps) => <Body1 medium {...typographyProps} />}
                  />
                )}
              </SensitiveData>
            </Box>
            <Box flex justify='space-between'>
              <Label>
                <Caption1>{renderSide(order.side, Caption1)}</Caption1>
              </Label>
              <Value>
                <SensitiveData>
                  <Caption1 color='text-med-em'>{sizeLots ?? '-'}</Caption1>
                </SensitiveData>
              </Value>
            </Box>
          </Box>

          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.OrderID')}</Label>
              <Value>{order.orderId}</Value>
            </Box>
            {positionEffect === DXTRADE_POSITION_EFFECT.OPEN && (
              <Box flex justify='space-between' mb='x8'>
                <Label>{t('Portfolio.OpenedOn')}</Label>
                <Value>{formatDateTime(order.issueTime)}</Value>
              </Box>
            )}
            {positionEffect === DXTRADE_POSITION_EFFECT.CLOSE && (
              <Box flex justify='space-between' mb='x8'>
                <Label>{t('Portfolio.ClosedOn')}</Label>
                <Value>{formatDateTime(order.transactionTime)}</Value>
              </Box>
            )}

            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Size')}</Label>
              <Value>
                <SensitiveData>{renderCellValue(sizeLots)}</SensitiveData>
              </Value>
            </Box>
            {positionEffect === DXTRADE_POSITION_EFFECT.OPEN && (
              <Box flex justify='space-between' mb='x8'>
                <Label>{t('Portfolio.OpenPrice')}</Label>
                <Value>
                  <SensitiveData>
                    {closePrice != null
                      ? formatNumber(closePrice, {
                          minDecimalPlaces: fractionDigits,
                          maxDecimalPlaces: fractionDigits,
                        })
                      : '—'}
                  </SensitiveData>
                </Value>
              </Box>
            )}
            {positionEffect === DXTRADE_POSITION_EFFECT.CLOSE && (
              <Box flex justify='space-between' mb='x8'>
                <Label>{t('Portfolio.ClosePrice')}</Label>
                <Value>
                  <SensitiveData>
                    {closePrice != null
                      ? formatNumber(closePrice, {
                          minDecimalPlaces: fractionDigits,
                          maxDecimalPlaces: fractionDigits,
                        })
                      : '—'}
                  </SensitiveData>
                </Value>
              </Box>
            )}
            {positionEffect === DXTRADE_POSITION_EFFECT.CLOSE && (
              <Box flex justify='space-between' mb='x8'>
                <Label>{t('Portfolio.P&L')}</Label>
                <SensitiveData>
                  <OrderPnL
                    order={order}
                    accountCurrency={accountCurrency}
                    mode='close'
                    Typography={Body2}
                  />
                </SensitiveData>
              </Box>
            )}
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Commission')}</Label>
              <Value>
                {commission ? (
                  <SensitiveData>
                    {formatSignedUsd(commission.total, commission.currency as CurrencyType)}
                  </SensitiveData>
                ) : (
                  '-'
                )}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.OvernightFees')}</Label>
              <Value>
                {resolvedOvernightFees ? (
                  <SensitiveData>
                    {formatSignedUsd(resolvedOvernightFees.total, accountCurrency)}
                  </SensitiveData>
                ) : (
                  '-'
                )}
              </Value>
            </Box>
            {positionEffect === DXTRADE_POSITION_EFFECT.CLOSE && (
              <Box flex justify='space-between' mb='x8'>
                <Label>{t('Portfolio.NetP&L')}</Label>
                <SensitiveData>{renderedNetPnl}</SensitiveData>
              </Box>
            )}
          </Box>
        </Box>
      </Modal.Body>
    </Modal>
  )
}

const Label: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-med-em'>{children}</Body2>
)

const Value: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-high-em'>{children}</Body2>
)
